/**
 * PTC Deal Pipeline is locked. Do not modify without explicit approval.
 * LOCKED: UI, layout, stages, buttons, logic — all frozen.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Plus, Mail, FileText, Send, Search, Loader2, RefreshCw, Receipt, ClipboardList,
  CheckCircle, UserCircle, AlertTriangle, XCircle, Clock, CheckCircle2,
  Phone, DollarSign, Eye, ChevronRight, Trash2, X, ArrowRight, Target, Zap, Lock,
  Link2, Copy, ExternalLink, CheckCheck, Download, FileCheck,
} from 'lucide-react';
import { toast } from 'sonner';
// v3.15-1221 CRM-INT-003 — read-only Compensation summary in the deal
// action panel. LOCKED file exception approved by user for CRM-INT-003.
// v3.21-1742: DealCompensationVisibilityCompact component import removed —
// no active rendering on Deal page.  Component file kept dormant.
import useDocumentTitle from '../hooks/useDocumentTitle';

// ═══════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════

const PIPELINE_STAGES = [
  { id: 'new_lead',            label: 'New Lead',            color: 'bg-slate-500',   lightBg: 'bg-slate-50',   border: 'border-slate-300',   text: 'text-slate-700' },
  { id: 'contacted',           label: 'Contacted',           color: 'bg-blue-500',    lightBg: 'bg-blue-50',    border: 'border-blue-300',    text: 'text-blue-700' },
  { id: 'credibility_sent',    label: 'Credibility Sent',    color: 'bg-indigo-500',  lightBg: 'bg-indigo-50',  border: 'border-indigo-300',  text: 'text-indigo-700' },
  { id: 'info_request_sent',   label: 'Info Request Sent',   color: 'bg-purple-500',  lightBg: 'bg-purple-50',  border: 'border-purple-300',  text: 'text-purple-700' },
  { id: 'info_received',       label: 'Info Received',       color: 'bg-amber-500',   lightBg: 'bg-amber-50',   border: 'border-amber-300',   text: 'text-amber-700' },
  { id: 'proposal_sent',       label: 'Proposal Sent',       color: 'bg-orange-500',  lightBg: 'bg-orange-50',  border: 'border-orange-300',  text: 'text-orange-700' },
  { id: 'contract_sent',       label: 'Contract Sent',       color: 'bg-cyan-500',    lightBg: 'bg-cyan-50',    border: 'border-cyan-300',    text: 'text-cyan-700' },
  { id: 'contract_signed',     label: 'Contract Signed',     color: 'bg-sky-600',     lightBg: 'bg-sky-50',     border: 'border-sky-300',     text: 'text-sky-700' },
  { id: 'payment_setup',       label: 'Payment Setup',       color: 'bg-rose-500',    lightBg: 'bg-rose-50',    border: 'border-rose-300',    text: 'text-rose-700' },
  { id: 'payment_in_progress', label: 'Payment In Progress', color: 'bg-pink-500',    lightBg: 'bg-pink-50',    border: 'border-pink-300',    text: 'text-pink-700' },
  { id: 'threshold_met',       label: 'Threshold Met',       color: 'bg-lime-600',    lightBg: 'bg-lime-50',    border: 'border-lime-300',    text: 'text-lime-700' },
  { id: 'submitted_to_partner',label: 'Submitted',           color: 'bg-teal-600',    lightBg: 'bg-teal-50',    border: 'border-teal-300',    text: 'text-teal-700' },
  { id: 'closed',              label: 'Closed',              color: 'bg-emerald-500', lightBg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-700' },
];

const NEXT_ACTION_MAP = {
  new_lead:            { label: 'Make Contact',           icon: Phone },
  contacted:           { label: 'Send Credibility Email', icon: Mail,          actionId: 'credibility_email' },
  credibility_sent:    { label: 'Send Info Request',      icon: ClipboardList, actionId: 'timeshare_info' },
  info_request_sent:   { label: 'Wait for Info',          icon: Clock },
  info_received:       { label: 'Create Proposal',        icon: FileText,      actionId: 'create_proposal' },
  proposal_sent:       { label: 'Send Contract',          icon: FileText,      actionId: 'send_contract' },
  contract_sent:       { label: 'Send Contract Link',    icon: FileText },
  contract_signed:     { label: 'Setup Payment',          icon: DollarSign },
  payment_setup:       { label: 'Collect Payment',        icon: DollarSign },
  payment_in_progress: { label: 'Collect Payment',        icon: DollarSign },
  threshold_met:       { label: 'Submit to Partner',      icon: Send },
  submitted_to_partner:{ label: 'Awaiting Partner',       icon: Clock },
  closed:              { label: 'Complete',                icon: CheckCircle },
};

const STAGE_ACTIONS = {
  new_lead:            ['credibility_email'],
  contacted:           ['credibility_email', 'timeshare_info'],
  credibility_sent:    ['timeshare_info'],
  info_request_sent:   ['timeshare_info'],
  info_received:       ['create_proposal', 'send_proposal'],
  proposal_sent:       ['send_contract'],
  contract_sent:       ['send_invoice'],
  contract_signed:     [],
  payment_setup:       [],
  payment_in_progress: [],
  threshold_met:       [],
  submitted_to_partner:[],
  closed:              [],
};

const WORKFLOW_ACTIONS = [
  { id: 'credibility_email', label: 'Send Credibility Email', icon: Mail,          color: 'bg-blue-600 hover:bg-blue-700',     requiresEmail: true },
  { id: 'timeshare_info',    label: 'Timeshare Info Request',  icon: ClipboardList, color: 'bg-purple-600 hover:bg-purple-700', requiresEmail: true },
  { id: 'create_proposal',   label: 'Create Proposal',        icon: FileText,      color: 'bg-amber-600 hover:bg-amber-700',   requiresEmail: false },
  { id: 'send_proposal',     label: 'Send Proposal',          icon: Send,          color: 'bg-indigo-600 hover:bg-indigo-700', requiresEmail: true },
  { id: 'send_contract',     label: 'Send Contract',          icon: FileText,      color: 'bg-cyan-600 hover:bg-cyan-700',     requiresEmail: true },
  { id: 'send_invoice',      label: 'Send Invoice',           icon: Receipt,       color: 'bg-orange-600 hover:bg-orange-700', requiresEmail: true },
];

const SOURCE_OPTIONS = [
  { value: 'funding_transfer', label: 'Funding' },
  { value: 'ads',              label: 'Ads' },
  { value: 'dialer',           label: 'Dialer' },
  { value: 'referral',         label: 'Referral' },
  { value: 'self_gen',         label: 'Self Gen' },
];

const PAYMENT_METHODS = [
  { value: 'ach',                label: 'ACH' },
  { value: 'invoice',           label: 'Invoice (Bill.com)' },
  { value: 'credit_card',       label: 'Credit Card' },
  { value: 'easyquickfund',     label: 'EasyQuickFund' },
  { value: 'in_house_finance',  label: 'In-House Finance' },
];

const THRESHOLD_DEFAULT = 1400;

// ═══════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════

function derivePipelineStage(c) {
  const ds = (c.deal_stage || '').toLowerCase();
  const p = c.ptc_progress || {};

  if (ds === 'submitted_to_partner') return 'submitted_to_partner';
  if (c.threshold_met) return 'threshold_met';
  if (c.payment_status === 'in_progress' || (c.amount_collected > 0 && !c.threshold_met)) return 'payment_in_progress';
  if (c.payment_method && (c.payment_status === 'pending' || (!c.amount_collected && ds !== 'submitted_to_partner'))) {
    if (p.contract_signed || ds === 'contract_signed') return 'payment_setup';
    if (p.contract_sent || ds === 'contract_sent') return 'payment_setup';
  }

  if (['funded', 'completed', 'closed', 'settlement', 'vendor_payment_received', 'customer_funded'].includes(ds)) return 'closed';
  if (p.paid) return 'closed';

  // Contract signed but no payment method set yet
  if (p.contract_signed || ds === 'contract_signed') return 'contract_signed';
  if (p.contract_sent || ds === 'contract_sent' || ds === 'invoice_sent') return 'contract_sent';
  if (p.proposal_sent || ds === 'proposal_sent') return 'proposal_sent';
  if (p.timeshare_info_received || p.proposal_created) return 'info_received';
  if (p.timeshare_info_requested) return 'info_request_sent';
  if (p.credibility_email_sent) return 'credibility_sent';
  if (ds === 'contacted') return 'contacted';
  return 'new_lead';
}

function fmtSource(v) {
  if (!v) return '--';
  const o = SOURCE_OPTIONS.find(s => s.value === v);
  return o ? o.label : v.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

function fmtMoney(v) { return v != null ? `$${Number(v).toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}` : '--'; }

function timeAgo(d) {
  if (!d) return '--';
  const ms = Date.now() - new Date(d).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

// ═══════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════

const PtcDealPipelinePage = () => {
  useDocumentTitle('PTC Deals');
  const { user } = useAuth();
  const navigate = useNavigate();

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [pipelineFilter, setPipelineFilter] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Dialog state
  const [selectClientOpen, setSelectClientOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [dialogClient, setDialogClient] = useState(null);
  const [senderOptions, setSenderOptions] = useState([]);
  const [selectedSender, setSelectedSender] = useState('logged_in_user');
  const [canChangeSender, setCanChangeSender] = useState(false);
  const [newDealOpen, setNewDealOpen] = useState(false);
  const [newDealData, setNewDealData] = useState({ client_id: '', resorts: [{ name: '', price: '' }], notes: '' });

  // Payment recording
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentData, setPaymentData] = useState({ amount: '', method: 'ach', notes: '' });
  const [paymentHistory, setPaymentHistory] = useState([]);

  // Magic link
  const [magicLinkOpen, setMagicLinkOpen] = useState(false);
  const [magicLinkData, setMagicLinkData] = useState(null);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Pending approvals
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [currentApproval, setCurrentApproval] = useState(null);
  const [declineReason, setDeclineReason] = useState('');

  // ─── Data Fetching ───
  const fetchClients = useCallback(async () => {
    try {
      const cr = await api.get('/companies');
      const ptc = (cr.data || []).find(c => c.name === 'Prestigious Travel Club Inc');
      if (!ptc) { setClients([]); return; }
      const r = await api.get('/clients', { params: { limit: 500, include_metrics: false, company_id: ptc.id } });
      setClients(r.data.clients || r.data || []);
    } catch (e) { console.error(e); toast.error('Failed to load clients'); }
  }, []);

  const fetchSenderOptions = useCallback(async () => {
    try {
      const r = await api.get('/workflow/sender-options');
      setSenderOptions(r.data.options || []);
      setCanChangeSender(r.data.can_change_sender || false);
    } catch (e) { console.error(e); }
  }, []);

  const fetchApprovals = useCallback(async () => {
    try { const r = await api.get('/deal-automation/approvals/pending'); setPendingApprovals(r.data || []); }
    catch (e) { console.error(e); }
  }, []);

  const fetchPayments = useCallback(async (clientId) => {
    try { const r = await api.get(`/deal-payments/${clientId}`); setPaymentHistory(r.data || []); }
    catch (e) { console.error(e); setPaymentHistory([]); }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchClients(), fetchSenderOptions(), fetchApprovals()]).finally(() => setLoading(false));
  }, [fetchClients, fetchSenderOptions, fetchApprovals]);

  useEffect(() => {
    if (selectedClient?.id) fetchPayments(selectedClient.id);
  }, [selectedClient?.id, fetchPayments]);

  const handleRefresh = () => {
    setRefreshing(true);
    Promise.all([fetchClients(), fetchApprovals()]).finally(() => setRefreshing(false));
  };

  // ─── Pipeline Computed ───
  const pipelineData = useMemo(() => {
    const counts = {}, values = {};
    PIPELINE_STAGES.forEach(s => { counts[s.id] = 0; values[s.id] = 0; });
    clients.forEach(c => {
      const s = derivePipelineStage(c);
      counts[s] = (counts[s] || 0) + 1;
      values[s] = (values[s] || 0) + (c.deal_amount || 0);
    });
    return { counts, values };
  }, [clients]);

  const filtered = useMemo(() => clients.filter(c => {
    if (pipelineFilter && derivePipelineStage(c) !== pipelineFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return c.full_name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) ||
             c.phone?.includes(q) || c.sales_rep_name?.toLowerCase().includes(q);
    }
    return true;
  }), [clients, pipelineFilter, searchQuery]);

  // ─── Magic Link Generation ───
  const generateMagicLink = async (linkType, client) => {
    setMagicLinkLoading(true);
    setLinkCopied(false);
    try {
      const r = await api.post('/sessions/create-and-send', {
        client_id: client.id,
        type: linkType,
        send_email: true,
      });
      setMagicLinkData(r.data);
      setMagicLinkOpen(true);
      if (r.data.email_sent) {
        toast.success(`Link sent to ${r.data.client_email}`);
      } else if (r.data.client_email) {
        toast.info('Link generated — email delivery may be delayed');
      } else {
        toast.info('Link generated — no email on file, copy the link to share');
      }
      fetchClients();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate link');
    } finally {
      setMagicLinkLoading(false);
    }
  };

  const copyMagicLink = () => {
    if (magicLinkData?.full_link) {
      navigator.clipboard.writeText(magicLinkData.full_link).then(() => {
        setLinkCopied(true);
        toast.success('Link copied to clipboard');
        setTimeout(() => setLinkCopied(false), 3000);
      }).catch(() => {
        // Fallback
        const el = document.createElement('textarea');
        el.value = magicLinkData.full_link;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
        setLinkCopied(true);
        toast.success('Link copied');
        setTimeout(() => setLinkCopied(false), 3000);
      });
    }
  };

  // ─── Workflow Actions ───
  const execWorkflow = async (actionId, client) => {
    // Route magic-link actions through the session system
    if (actionId === 'timeshare_info') {
      return generateMagicLink('info_request', client);
    }
    if (actionId === 'send_contract') {
      return generateMagicLink('contract', client);
    }

    setActionLoading(true);
    try {
      const body = { sender_id: selectedSender };
      const endpoints = {
        credibility_email: `/clients/${client.id}/workflow/credibility-email`,
        create_proposal: `/clients/${client.id}/workflow/create-proposal`,
        send_proposal: `/clients/${client.id}/workflow/send-proposal`,
        send_invoice: `/clients/${client.id}/workflow/send-invoice`,
      };
      if (endpoints[actionId]) {
        await api.post(endpoints[actionId], ['create_proposal'].includes(actionId) ? undefined : body);
        toast.success(`${WORKFLOW_ACTIONS.find(a => a.id === actionId)?.label || actionId} completed`);
        fetchClients();
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setActionLoading(false); }
  };

  const handleActionClick = (action) => {
    setSelectedAction(action);
    setSelectedSender('logged_in_user');
    setSelectClientOpen(true);
  };

  const executeDialogAction = async () => {
    if (!dialogClient || !selectedAction) return;
    await execWorkflow(selectedAction.id, dialogClient);
    setSelectClientOpen(false); setDialogClient(null); setSelectedAction(null);
  };

  // ─── Payment Recording (Phase 4) ───
  const recordPayment = async () => {
    if (!selectedClient || !paymentData.amount) return;
    setActionLoading(true);
    try {
      const r = await api.post('/deal-payments', {
        client_id: selectedClient.id,
        amount: parseFloat(paymentData.amount),
        method: paymentData.method,
        notes: paymentData.notes || undefined,
      });
      toast.success(`Payment of $${parseFloat(paymentData.amount).toLocaleString()} recorded`);
      // Update local state
      setClients(prev => prev.map(c => c.id === selectedClient.id ? {
        ...c,
        amount_collected: r.data.amount_collected,
        remaining_balance: r.data.remaining_balance,
        threshold_met: r.data.threshold_met,
        commission_earned: r.data.commission_earned,
        commission_remaining: r.data.commission_remaining,
        payment_status: 'in_progress',
      } : c));
      setSelectedClient(prev => prev ? {
        ...prev,
        amount_collected: r.data.amount_collected,
        remaining_balance: r.data.remaining_balance,
        threshold_met: r.data.threshold_met,
        commission_earned: r.data.commission_earned,
        commission_remaining: r.data.commission_remaining,
        payment_status: 'in_progress',
      } : null);
      fetchPayments(selectedClient.id);
      setPaymentOpen(false);
      setPaymentData({ amount: '', method: 'ach', notes: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Payment recording failed'); }
    finally { setActionLoading(false); }
  };

  // ─── Payment Method ───
  const setPaymentMethod = async (clientId, method) => {
    setActionLoading(true);
    try {
      await api.put(`/clients/${clientId}`, { payment_method: method, payment_status: 'pending' });
      setClients(prev => prev.map(c => c.id === clientId ? { ...c, payment_method: method, payment_status: 'pending' } : c));
      setSelectedClient(prev => prev ? { ...prev, payment_method: method, payment_status: 'pending' } : null);
      toast.success(`Payment method: ${PAYMENT_METHODS.find(m => m.value === method)?.label}`);
    } catch (e) { toast.error('Failed to set payment method'); }
    finally { setActionLoading(false); }
  };

  // ─── Submit to Partner (Phase 6 Lock) ───
  const submitToPartner = async (clientId) => {
    setActionLoading(true);
    try {
      await api.put(`/clients/${clientId}`, { deal_stage: 'submitted_to_partner' });
      setClients(prev => prev.map(c => c.id === clientId ? { ...c, deal_stage: 'submitted_to_partner' } : c));
      setSelectedClient(prev => prev ? { ...prev, deal_stage: 'submitted_to_partner' } : null);
      toast.success('Deal submitted to partner');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit'); }
    finally { setActionLoading(false); }
  };

  // ─── Source + Commission Update ───
  const updateField = async (clientId, field, value) => {
    try {
      await api.put(`/clients/${clientId}`, { [field]: value });
      setClients(prev => prev.map(c => c.id === clientId ? { ...c, [field]: value } : c));
      setSelectedClient(prev => prev ? { ...prev, [field]: value } : null);
    } catch (e) { toast.error('Update failed'); }
  };

  // ─── Approvals ───
  const handleApprove = async (a) => {
    setActionLoading(true);
    try {
      await api.post('/deal-automation/approvals/decide', { deal_id: a.deal_id, approval_type: a.approval_type, decision: 'approve' });
      toast.success('Approved'); fetchApprovals();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setActionLoading(false); }
  };

  const handleDecline = async () => {
    if (!currentApproval) return;
    setActionLoading(true);
    try {
      await api.post('/deal-automation/approvals/decide', { deal_id: currentApproval.deal_id, approval_type: currentApproval.approval_type, decision: 'decline', reason: declineReason || undefined });
      toast.success('Declined'); setDeclineDialogOpen(false); fetchApprovals();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setActionLoading(false); }
  };

  const handleCreateDeal = async () => {
    if (!newDealData.client_id || !newDealData.resorts[0]?.name) { toast.error('Client + resort required'); return; }
    setActionLoading(true);
    try {
      const r = await api.post('/deal-automation/deals', {
        client_id: newDealData.client_id,
        resorts: newDealData.resorts.filter(r => r.name).map(r => ({ name: r.name, price: parseFloat(r.price) || 0 })),
        notes: newDealData.notes,
      });
      toast.success('Deal created');
      if (r.data?.id) { try { await api.post(`/deal-automation/deals/${r.data.id}/proposal`); } catch (e) { console.warn('[PtcDealPipeline] auto-proposal generation failed', e); toast.warning('Deal created but proposal auto-draft failed — open the deal to retry.'); } }
      setNewDealOpen(false);
      setNewDealData({ client_id: '', resorts: [{ name: '', price: '' }], notes: '' });
      fetchClients(); fetchApprovals();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setActionLoading(false); }
  };

  // ─── Render ───
  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-accent" /></div>;

  const totalVal = clients.reduce((s, c) => s + (c.deal_amount || 0), 0);
  const activeStage = pipelineFilter ? PIPELINE_STAGES.find(s => s.id === pipelineFilter) : null;

  return (
    <div className="space-y-4" data-testid="ptc-deals-pipeline">
      {/* HEADER */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800" data-testid="pipeline-title">PTC Deal Pipeline</h1>
          <p className="text-sm text-slate-500">{clients.length} deals &middot; {fmtMoney(totalVal)} total</p>
        </div>
        <div className="flex gap-2">
          <Button className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2" onClick={() => setNewDealOpen(true)} data-testid="action-new_deal">
            <Plus className="h-4 w-4" /> New Deal
          </Button>
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing} className="gap-2" data-testid="refresh-btn">
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* PHASE 1 — PIPELINE BAR */}
      <div className="flex gap-1 overflow-x-auto pb-1" data-testid="pipeline-bar">
        {PIPELINE_STAGES.map(stage => {
          const cnt = pipelineData.counts[stage.id] || 0;
          const val = pipelineData.values[stage.id] || 0;
          const active = pipelineFilter === stage.id;
          return (
            <button key={stage.id} onClick={() => setPipelineFilter(active ? null : stage.id)}
              className={`relative p-2.5 rounded-lg border-2 text-left transition-all cursor-pointer min-w-[100px] flex-1 ${
                active ? `${stage.lightBg} ${stage.border} shadow-sm` : 'bg-white border-slate-200 hover:border-slate-300'}`}
              data-testid={`pipeline-stage-${stage.id}`}
            >
              <div className={`w-full h-1 ${stage.color} rounded-full mb-1.5 ${active ? 'opacity-100' : 'opacity-40'}`} />
              <p className={`text-[10px] font-semibold truncate ${active ? stage.text : 'text-slate-500'}`}>{stage.label}</p>
              <p className="text-base font-bold text-slate-900 leading-tight">{cnt}</p>
              <p className="text-[10px] text-slate-400">{fmtMoney(val)}</p>
            </button>
          );
        })}
      </div>

      {activeStage && (
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${activeStage.lightBg} ${activeStage.border} border`}>
          <Target className={`h-3.5 w-3.5 ${activeStage.text}`} />
          <span className={`text-xs font-medium ${activeStage.text}`}>{activeStage.label} ({pipelineData.counts[activeStage.id]})</span>
          <button onClick={() => setPipelineFilter(null)} className="ml-auto text-slate-400 hover:text-slate-600"><X className="h-3.5 w-3.5" /></button>
        </div>
      )}

      {/* APPROVALS */}
      {pendingApprovals.length > 0 && (
        <Card className="border-orange-400 bg-orange-50/50" data-testid="pending-approvals">
          <CardContent className="py-3 space-y-2">
            <p className="text-xs font-semibold text-orange-800 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> {pendingApprovals.length} Pending Approvals</p>
            {pendingApprovals.map(a => (
              <div key={a.id} className="flex items-center justify-between p-2 bg-white rounded border border-orange-200">
                <div><p className="text-xs font-medium">{a.prompt_message}</p><p className="text-[10px] text-slate-500">{a.deal_info?.primary_owner_name} &middot; {fmtMoney(a.deal_info?.total_amount)}</p></div>
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={() => { setCurrentApproval(a); setDeclineDialogOpen(true); }}><XCircle className="h-3 w-3 mr-0.5" />Decline</Button>
                  <Button size="sm" className="h-6 text-[10px] bg-green-600 hover:bg-green-700" onClick={() => handleApprove(a)} disabled={actionLoading}><CheckCircle2 className="h-3 w-3 mr-0.5" />Approve</Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* SEARCH + QUICK ACTIONS */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 h-9" data-testid="deal-search" />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {WORKFLOW_ACTIONS.map(a => (
            <Button key={a.id} variant="outline" size="sm" className="text-[10px] gap-1 h-7" onClick={() => handleActionClick(a)} data-testid={`action-${a.id}`}>
              <a.icon className="h-3 w-3" />{a.label}
            </Button>
          ))}
        </div>
      </div>

      {/* PHASE 2 — DEAL TABLE */}
      <Card data-testid="deal-list-card">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs font-semibold w-[180px]">Client Name</TableHead>
                <TableHead className="text-xs font-semibold w-[80px]">Source</TableHead>
                <TableHead className="text-xs font-semibold w-[120px]">Stage</TableHead>
                <TableHead className="text-xs font-semibold w-[160px]">Next Action</TableHead>
                <TableHead className="text-xs font-semibold text-right w-[90px]">Total Deal</TableHead>
                <TableHead className="text-xs font-semibold text-right w-[90px]">Collected</TableHead>
                <TableHead className="text-xs font-semibold w-[110px]">Rep</TableHead>
                <TableHead className="text-xs font-semibold w-[40px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-400"><FileText className="h-8 w-8 mx-auto mb-1 text-slate-300" /><p className="text-xs">No deals found</p></TableCell></TableRow>
              ) : filtered.map(c => {
                const stage = derivePipelineStage(c);
                const sc = PIPELINE_STAGES.find(s => s.id === stage);
                const na = NEXT_ACTION_MAP[stage];
                const sel = selectedClient?.id === c.id;
                return (
                  <TableRow key={c.id} className={`cursor-pointer transition-colors ${sel ? 'bg-accent/5 border-l-2 border-l-accent' : 'hover:bg-slate-50'}`}
                    onClick={() => setSelectedClient(sel ? null : c)} data-testid={`deal-row-${c.id}`}>
                    <TableCell className="py-2.5">
                      <div className="flex items-center gap-2">
                        <div className={`w-7 h-7 rounded-full ${sc?.color} flex items-center justify-center text-white text-[10px] font-bold shrink-0`}>{c.full_name?.charAt(0)}</div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="text-sm font-medium text-slate-800 truncate cursor-pointer hover:text-blue-600 hover:underline" onClick={(e) => { e.stopPropagation(); navigate(`/clients/${c.id}`); }}>{c.full_name}</p>
                            {c.banking_account_holder && <span className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-semibold whitespace-nowrap" data-testid={`ach-badge-${c.id}`}>ACH Received</span>}
                          </div>
                          <p className="text-[10px] text-slate-400 truncate">{c.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell><span className="text-xs text-slate-600">{fmtSource(c.deal_source)}</span></TableCell>
                    <TableCell><Badge className={`${sc?.color} text-white text-[10px] px-1.5 py-0`}>{sc?.label}</Badge></TableCell>
                    <TableCell><div className="flex items-center gap-1">{na?.icon && <na.icon className="h-3 w-3 text-slate-400" />}<span className="text-xs text-slate-700">{na?.label}</span></div></TableCell>
                    <TableCell className="text-right"><span className="text-sm font-semibold">{fmtMoney(c.deal_amount)}</span></TableCell>
                    <TableCell className="text-right"><span className={`text-sm font-semibold ${c.threshold_met ? 'text-emerald-600' : c.amount_collected > 0 ? 'text-amber-600' : 'text-slate-400'}`}>{c.amount_collected > 0 ? fmtMoney(c.amount_collected) : '--'}</span></TableCell>
                    <TableCell><span className="text-xs text-slate-600">{c.sales_rep_name || '--'}</span></TableCell>
                    <TableCell><ChevronRight className={`h-4 w-4 transition-transform ${sel ? 'rotate-90 text-accent' : 'text-slate-300'}`} /></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* DEAL VIEW PANEL */}
      {selectedClient && (() => {
        const stage = derivePipelineStage(selectedClient);
        const sc = PIPELINE_STAGES.find(s => s.id === stage);
        const na = NEXT_ACTION_MAP[stage];
        const relevantActions = WORKFLOW_ACTIONS.filter(a => (STAGE_ACTIONS[stage] || []).includes(a.id));
        const threshold = selectedClient.threshold_required || THRESHOLD_DEFAULT;
        const collected = selectedClient.amount_collected || 0;
        const remaining = (selectedClient.deal_amount || 0) - collected;
        const thresholdPct = threshold > 0 ? Math.min((collected / threshold) * 100, 100) : 0;
        const commRate = selectedClient.commission_rate || 0;
        const commEarned = selectedClient.commission_earned || 0;
        const commTotal = (selectedClient.deal_amount || 0) * commRate;
        const commRemaining = commTotal - commEarned;
        const showPayment = ['contract_signed', 'contract_sent', 'payment_setup', 'payment_in_progress', 'threshold_met'].includes(stage);

        return (
          <Card className={`border-2 ${sc?.border}`} data-testid="deal-action-panel">
            <CardHeader className="pb-2 pt-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-full ${sc?.color} flex items-center justify-center text-white font-bold text-sm`}>{selectedClient.full_name?.charAt(0)}</div>
                  <div>
                    <CardTitle className="text-base">{selectedClient.full_name}</CardTitle>
                    <p className="text-[10px] text-slate-500">{selectedClient.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`${sc?.color} text-white text-xs`}>{sc?.label}</Badge>
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => navigate(`/clients/${selectedClient.id}`)} data-testid="view-client-btn"><Eye className="h-3.5 w-3.5 mr-1" />Profile</Button>
                  <button onClick={() => setSelectedClient(null)} className="text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3 pt-0">
              {/* Next Action */}
              {na && stage !== 'closed' && (
                <div className={`flex items-center justify-between p-2.5 rounded-lg ${sc?.lightBg} border ${sc?.border}`}>
                  <div className="flex items-center gap-2">
                    <Zap className={`h-4 w-4 ${sc?.text}`} />
                    <p className={`text-sm font-semibold ${sc?.text}`}>{na.label}</p>
                  </div>
                  {na.actionId && (
                    <Button size="sm" className={`${sc?.color} text-white gap-1 text-xs h-7`} onClick={() => execWorkflow(na.actionId, selectedClient)} disabled={actionLoading} data-testid="execute-next-action">
                      {actionLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5" />}{na.label}
                    </Button>
                  )}
                </div>
              )}

              {/* Workflow Actions */}
              {relevantActions.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {relevantActions.map(a => (
                    <Button key={a.id} size="sm" className={`${a.color} text-white gap-1 text-[10px] h-7`} onClick={() => execWorkflow(a.id, selectedClient)} disabled={actionLoading || magicLinkLoading} data-testid={`panel-action-${a.id}`}>
                      {(actionLoading || magicLinkLoading) ? <Loader2 className="h-3 w-3 animate-spin" /> : <a.icon className="h-3 w-3" />}{a.label}
                    </Button>
                  ))}
                </div>
              )}

              {/* Magic Link Buttons (shown in relevant stages) */}
              {['credibility_sent', 'info_request_sent', 'info_received', 'proposal_sent', 'contract_sent'].includes(stage) && (
                <div className="flex flex-wrap gap-1.5">
                  {['credibility_sent', 'info_request_sent'].includes(stage) && (
                    <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white gap-1.5 text-xs h-8" onClick={() => generateMagicLink('info_request', selectedClient)} disabled={magicLinkLoading} data-testid="send-info-request-btn">
                      {magicLinkLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />}
                      Send Info Request Link
                    </Button>
                  )}
                  {['info_received', 'proposal_sent', 'contract_sent'].includes(stage) && (
                    <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white gap-1.5 text-xs h-8" onClick={() => generateMagicLink('contract', selectedClient)} disabled={magicLinkLoading} data-testid="send-contract-btn">
                      {magicLinkLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />}
                      Send Contract Link
                    </Button>
                  )}
                </div>
              )}

              {/* Source + Commission Rate */}
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-medium text-slate-500 uppercase">Source:</span>
                  <Select value={selectedClient.deal_source || ''} onValueChange={v => updateField(selectedClient.id, 'deal_source', v)}>
                    <SelectTrigger className="w-32 h-7 text-xs" data-testid="source-select"><SelectValue placeholder="Set..." /></SelectTrigger>
                    <SelectContent>{SOURCE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-medium text-slate-500 uppercase">Commission:</span>
                  <Select value={String(selectedClient.commission_rate || '')} onValueChange={v => updateField(selectedClient.id, 'commission_rate', parseFloat(v))}>
                    <SelectTrigger className="w-28 h-7 text-xs" data-testid="commission-rate-select"><SelectValue placeholder="Rate..." /></SelectTrigger>
                    <SelectContent>
                      {[0.05, 0.08, 0.10, 0.12, 0.15, 0.20].map(r => <SelectItem key={r} value={String(r)}>{(r * 100).toFixed(0)}%</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* ═══ PAYMENT + COMMISSION PANELS ═══ */}
              {showPayment && (
                <div className="grid grid-cols-2 gap-3">
                  {/* PAYMENT PANEL */}
                  <div className="border-2 border-rose-200 rounded-lg p-3 bg-rose-50/30 space-y-2" data-testid="payment-panel">
                    <p className="text-xs font-bold text-rose-800 flex items-center gap-1.5"><DollarSign className="h-3.5 w-3.5" />Payment</p>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div><p className="text-[10px] text-slate-500">Total</p><p className="text-sm font-bold">{fmtMoney(selectedClient.deal_amount)}</p></div>
                      <div><p className="text-[10px] text-slate-500">Collected</p><p className={`text-sm font-bold ${collected > 0 ? 'text-emerald-600' : ''}`}>{fmtMoney(collected)}</p></div>
                      <div><p className="text-[10px] text-slate-500">Remaining</p><p className="text-sm font-bold text-rose-600">{fmtMoney(remaining > 0 ? remaining : 0)}</p></div>
                    </div>

                    {/* Threshold bar */}
                    <div>
                      <div className="flex justify-between text-[10px] mb-0.5">
                        <span className={selectedClient.threshold_met ? 'text-emerald-600 font-bold' : 'text-rose-600'}>
                          {selectedClient.threshold_met ? 'Threshold Met' : `$${threshold.toLocaleString()} threshold`}
                        </span>
                        <span className="text-slate-500">{thresholdPct.toFixed(0)}%</span>
                      </div>
                      <div className="w-full bg-rose-200 rounded-full h-2">
                        <div className={`h-2 rounded-full transition-all ${selectedClient.threshold_met ? 'bg-emerald-500' : 'bg-rose-500'}`} style={{ width: `${thresholdPct}%` }} />
                      </div>
                    </div>

                    {/* Payment Method */}
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-500">Method:</span>
                      {selectedClient.payment_method ? (
                        <Badge className="bg-rose-500 text-white text-[10px]">{PAYMENT_METHODS.find(m => m.value === selectedClient.payment_method)?.label || selectedClient.payment_method}</Badge>
                      ) : (
                        <Select onValueChange={v => setPaymentMethod(selectedClient.id, v)}>
                          <SelectTrigger className="w-36 h-6 text-[10px]" data-testid="payment-method-select"><SelectValue placeholder="Select method..." /></SelectTrigger>
                          <SelectContent>{PAYMENT_METHODS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                    </div>

                    {/* Record Payment Button */}
                    {selectedClient.payment_method && (
                      <Button size="sm" className="w-full bg-rose-600 hover:bg-rose-700 text-white text-xs h-8 gap-1.5" onClick={() => setPaymentOpen(true)} disabled={actionLoading} data-testid="record-payment-btn">
                        <DollarSign className="h-3.5 w-3.5" /> Record Payment
                      </Button>
                    )}

                    {/* Payment History */}
                    {paymentHistory.length > 0 && (
                      <div>
                        <p className="text-[10px] text-slate-500 mb-1">{paymentHistory.length} payment(s):</p>
                        <div className="space-y-1 max-h-24 overflow-y-auto">
                          {paymentHistory.map(p => (
                            <div key={p.id} className="flex items-center justify-between text-[10px] bg-white rounded px-2 py-1 border">
                              <span className="font-medium">{fmtMoney(p.amount)}</span>
                              <span className="text-slate-400">{p.method}</span>
                              <span className="text-slate-400">{timeAgo(p.date)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* COMMISSION PANEL */}
                  <div className="border-2 border-indigo-200 rounded-lg p-3 bg-indigo-50/30 space-y-2" data-testid="commission-panel">
                    <p className="text-xs font-bold text-indigo-800 flex items-center gap-1.5"><Receipt className="h-3.5 w-3.5" />Commission</p>
                    {commRate > 0 ? (
                      <>
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div><p className="text-[10px] text-slate-500">Rate</p><p className="text-sm font-bold">{(commRate * 100).toFixed(0)}%</p></div>
                          <div><p className="text-[10px] text-slate-500">Earned</p><p className="text-sm font-bold text-emerald-600">{fmtMoney(commEarned)}</p></div>
                          <div><p className="text-[10px] text-slate-500">Remaining</p><p className="text-sm font-bold text-indigo-600">{fmtMoney(commRemaining > 0 ? commRemaining : 0)}</p></div>
                        </div>
                        <div>
                          <div className="flex justify-between text-[10px] mb-0.5">
                            <span className="text-indigo-600">Commission Progress</span>
                            <span className="text-slate-500">{commTotal > 0 ? ((commEarned / commTotal) * 100).toFixed(0) : 0}%</span>
                          </div>
                          <div className="w-full bg-indigo-200 rounded-full h-2">
                            <div className="h-2 rounded-full bg-indigo-500 transition-all" style={{ width: `${commTotal > 0 ? Math.min((commEarned / commTotal) * 100, 100) : 0}%` }} />
                          </div>
                        </div>
                      </>
                    ) : (
                      <p className="text-xs text-slate-400">Set commission rate to track earnings</p>
                    )}
                  </div>
                </div>
              )}

              {/* v3.21-1742: Compact V1 Compensation Visibility rendering
                  removed from Deal Pipeline flow — the visible card was
                  removed per operator request (never functioned in
                  practice).  The DealCompensationVisibilityCompact
                  component is left in-source but no longer rendered
                  anywhere on the Deal page.  Nothing else on this
                  pipeline card is affected. */}

              {/* PHASE 6 — SUBMIT TO PARTNER (locked if threshold not met) */}
              {stage === 'threshold_met' && (
                <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white gap-2" onClick={() => submitToPartner(selectedClient.id)} disabled={actionLoading} data-testid="submit-to-partner-btn">
                  {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Submit to Partner
                </Button>
              )}
              {(stage === 'payment_setup' || stage === 'payment_in_progress') && !selectedClient.threshold_met && (
                <div className="flex items-center gap-2 p-2.5 bg-rose-50 rounded-lg border border-rose-300" data-testid="threshold-lock">
                  <Lock className="h-4 w-4 text-rose-500" />
                  <span className="text-xs font-medium text-rose-700">Minimum ${threshold.toLocaleString()} must be collected before submission</span>
                </div>
              )}

              {/* Signed Contract Actions */}
              {(selectedClient.contract_id || selectedClient.ptc_progress?.contract_signed) && (
                <div className="border-2 border-emerald-200 rounded-lg p-3 bg-emerald-50/30" data-testid="contract-panel">
                  <p className="text-xs font-bold text-emerald-800 flex items-center gap-1.5 mb-2"><FileCheck className="h-3.5 w-3.5" />Signed Contract</p>
                  <div className="text-xs text-slate-600 space-y-1 mb-3">
                    {selectedClient.contract_signed_name && <p>Signed by: <span className="font-medium text-slate-800">{selectedClient.contract_signed_name}</span></p>}
                    {selectedClient.contract_signed_at && <p>Date: <span className="font-medium text-slate-800">{new Date(selectedClient.contract_signed_at).toLocaleDateString()}</span></p>}
                  </div>
                  {selectedClient.contract_id ? (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="flex-1 gap-1.5 text-xs h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/contracts/${selectedClient.contract_id}/view`, '_blank')}
                        data-testid="view-contract-btn">
                        <Eye className="h-3.5 w-3.5" /> View Contract
                      </Button>
                      <Button size="sm" variant="outline" className="flex-1 gap-1.5 text-xs h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        onClick={() => { const a = document.createElement('a'); a.href = `${process.env.REACT_APP_BACKEND_URL}/api/contracts/${selectedClient.contract_id}/download`; a.download = ''; document.body.appendChild(a); a.click(); document.body.removeChild(a); }}
                        data-testid="download-contract-btn">
                        <Download className="h-3.5 w-3.5" /> Download PDF
                      </Button>
                    </div>
                  ) : (
                    <p className="text-[10px] text-slate-400">Contract PDF will be available after signing via magic link</p>
                  )}
                </div>
              )}

              {/* Progress steps */}
              {selectedClient.ptc_progress && Object.keys(selectedClient.ptc_progress).filter(k => selectedClient.ptc_progress[k]).length > 0 && (
                <div>
                  <p className="text-[10px] font-medium text-slate-500 uppercase mb-1">Completed Steps</p>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(selectedClient.ptc_progress).filter(([,v]) => v).map(([k]) => (
                      <Badge key={k} variant="secondary" className="text-[10px]"><CheckCircle className="h-2.5 w-2.5 mr-0.5 text-emerald-500" />{k.replace(/_/g, ' ')}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* ═══ DIALOGS ═══ */}

      {/* Record Payment Dialog */}
      <Dialog open={paymentOpen} onOpenChange={setPaymentOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><DollarSign className="h-5 w-5 text-rose-600" />Record Payment</DialogTitle>
            <DialogDescription>Record a payment for {selectedClient?.full_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-3">
            <div>
              <Label className="text-xs">Amount *</Label>
              <Input type="number" placeholder="0.00" value={paymentData.amount} onChange={e => setPaymentData({...paymentData, amount: e.target.value})} data-testid="payment-amount" />
            </div>
            <div>
              <Label className="text-xs">Method</Label>
              <Select value={paymentData.method} onValueChange={v => setPaymentData({...paymentData, method: v})}>
                <SelectTrigger data-testid="payment-dialog-method"><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_METHODS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea placeholder="Payment notes..." value={paymentData.notes} onChange={e => setPaymentData({...paymentData, notes: e.target.value})} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentOpen(false)}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white gap-1.5" onClick={recordPayment} disabled={actionLoading || !paymentData.amount} data-testid="confirm-payment-btn">
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <DollarSign className="h-4 w-4" />}
              Record ${paymentData.amount ? parseFloat(paymentData.amount).toLocaleString() : '0'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Selection Dialog (for quick actions) */}
      <Dialog open={selectClientOpen} onOpenChange={setSelectClientOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">{selectedAction && <selectedAction.icon className="h-5 w-5" />}{selectedAction?.label}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto py-3">
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Search clients..." className="pl-10" onChange={e => setSearchQuery(e.target.value)} />
            </div>
            <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
              {clients.filter(c => !searchQuery || c.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) || c.email?.toLowerCase().includes(searchQuery.toLowerCase())).map(c => (
                <div key={c.id} className={`flex items-center justify-between p-2.5 border rounded-lg cursor-pointer transition-colors ${dialogClient?.id === c.id ? 'bg-accent/10 border-accent' : 'hover:bg-slate-50'}`}
                  onClick={() => setDialogClient(c)}>
                  <div className="flex items-center gap-2"><div className="w-7 h-7 rounded-full bg-accent flex items-center justify-center text-white text-xs font-bold">{c.full_name?.charAt(0)}</div>
                    <div><p className="text-sm font-medium">{c.full_name}</p><p className="text-[10px] text-slate-500">{c.email}</p></div>
                  </div>
                  {dialogClient?.id === c.id && <CheckCircle className="h-4 w-4 text-accent" />}
                </div>
              ))}
            </div>
            {selectedAction?.requiresEmail && canChangeSender && senderOptions.length > 0 && (
              <div className="mt-3 p-3 bg-slate-50 rounded-lg border">
                <Label className="text-xs flex items-center gap-1 mb-1.5"><UserCircle className="h-3.5 w-3.5" />Send From</Label>
                <Select value={selectedSender} onValueChange={setSelectedSender}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{senderOptions.map(s => <SelectItem key={s.id} value={s.id}>{s.name} ({s.email})</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectClientOpen(false)}>Cancel</Button>
            <Button onClick={executeDialogAction} disabled={!dialogClient || actionLoading} className="gap-1.5">
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : selectedAction && <selectedAction.icon className="h-4 w-4" />}{selectedAction?.label}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Deal Dialog */}
      <Dialog open={newDealOpen} onOpenChange={setNewDealOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Create New PTC Deal</DialogTitle><DialogDescription>Select a client and add resorts.</DialogDescription></DialogHeader>
          <div className="space-y-3 py-3">
            <div><Label>Client *</Label>
              <Select value={newDealData.client_id} onValueChange={v => setNewDealData({...newDealData, client_id: v})}>
                <SelectTrigger><SelectValue placeholder="Choose client" /></SelectTrigger>
                <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.full_name} ({c.email})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Resorts *</Label>
              {newDealData.resorts.map((r, i) => (
                <div key={`resort-${i}-${r.name || ''}`} className="flex gap-2 mt-1">
                  <Input value={r.name} onChange={e => { const u=[...newDealData.resorts]; u[i].name=e.target.value; setNewDealData({...newDealData, resorts:u}); }} placeholder="Resort name" className="flex-1" />
                  <Input type="number" value={r.price} onChange={e => { const u=[...newDealData.resorts]; u[i].price=e.target.value; setNewDealData({...newDealData, resorts:u}); }} placeholder="Price" className="w-28" />
                  {newDealData.resorts.length > 1 && <Button variant="ghost" size="icon" onClick={() => setNewDealData({...newDealData, resorts: newDealData.resorts.filter((_,j)=>j!==i)})}><XCircle className="h-4 w-4" /></Button>}
                </div>
              ))}
              <Button variant="ghost" size="sm" className="mt-1" onClick={() => setNewDealData({...newDealData, resorts:[...newDealData.resorts,{name:'',price:''}]})}><Plus className="h-4 w-4 mr-1" />Add Resort</Button>
            </div>
            <div><Label>Notes</Label><Textarea value={newDealData.notes} onChange={e => setNewDealData({...newDealData, notes: e.target.value})} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewDealOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateDeal} disabled={actionLoading}>{actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}Create Deal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decline Dialog */}
      <Dialog open={declineDialogOpen} onOpenChange={setDeclineDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Decline Approval</DialogTitle><DialogDescription>Provide a reason for declining.</DialogDescription></DialogHeader>
          <div className="py-3"><Label>Reason</Label><Textarea value={declineReason} onChange={e => setDeclineReason(e.target.value)} rows={3} className="mt-1" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeclineDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDecline} disabled={actionLoading}><XCircle className="h-4 w-4 mr-1" />Decline</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Magic Link Dialog */}
      <Dialog open={magicLinkOpen} onOpenChange={setMagicLinkOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-teal-600" />
              {magicLinkData?.type === 'info_request' ? 'Info Request Link' : magicLinkData?.type === 'contract' ? 'Contract Signing Link' : 'Magic Link'}
            </DialogTitle>
            <DialogDescription>
              {magicLinkData?.type === 'info_request'
                ? 'Share this link with the client to collect their timeshare information.'
                : 'Share this link with the client to review and sign the service agreement.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-3">
            {/* Status */}
            <div className={`flex items-center gap-2 p-3 rounded-lg border ${
              magicLinkData?.email_sent
                ? 'bg-emerald-50 border-emerald-200'
                : 'bg-amber-50 border-amber-200'
            }`}>
              {magicLinkData?.email_sent ? (
                <>
                  <CheckCircle className="h-4 w-4 text-emerald-600 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-emerald-800">Email sent successfully</p>
                    <p className="text-xs text-emerald-600">Sent to {magicLinkData?.client_email}</p>
                  </div>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">
                      {magicLinkData?.client_email ? 'Email delivery pending' : 'No email on file'}
                    </p>
                    <p className="text-xs text-amber-600">Copy the link below to share manually</p>
                  </div>
                </>
              )}
            </div>

            {/* Link */}
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Client Link (no login required)</Label>
              <div className="flex items-center gap-2">
                <Input
                  value={magicLinkData?.full_link || ''}
                  readOnly
                  className="text-xs font-mono bg-slate-50"
                  data-testid="magic-link-url"
                />
                <Button
                  variant="outline"
                  size="sm"
                  className={`shrink-0 gap-1.5 ${linkCopied ? 'text-emerald-600 border-emerald-300' : ''}`}
                  onClick={copyMagicLink}
                  data-testid="copy-magic-link-btn"
                >
                  {linkCopied ? <CheckCheck className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {linkCopied ? 'Copied' : 'Copy'}
                </Button>
              </div>
            </div>

            {/* Client info */}
            <div className="text-xs text-slate-500 space-y-1">
              <p>Client: <span className="font-medium text-slate-700">{magicLinkData?.client_name}</span></p>
              <p>Expires: <span className="font-medium text-slate-700">{magicLinkData?.expires_at ? new Date(magicLinkData.expires_at).toLocaleDateString() : '--'}</span></p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setMagicLinkOpen(false)}>Close</Button>
            {magicLinkData?.full_link && (
              <Button
                variant="outline"
                className="gap-1.5"
                onClick={() => window.open(magicLinkData.full_link, '_blank')}
                data-testid="preview-magic-link-btn"
              >
                <ExternalLink className="h-3.5 w-3.5" /> Preview
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PtcDealPipelinePage;
